import React, { useEffect, useState } from 'react';
import Select, { StylesConfig } from 'react-select';
import { getYear } from 'date-fns';
import { toNumber } from 'lodash';

interface YearSelectProps {
    date: Date;
    changeYear: (year: number) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

const yearStyles: StylesConfig = {
    indicatorSeparator: (base, state) => {
        return { ...base, display: 'none' };
    },
    dropdownIndicator: (base, state) => {
        return { ...base, padding: 0 };
    },
    menuList: (base, state) => {
        return { ...base, height: '15rem', overflowY: 'auto', scrollbarWidth: 'thin', width: '5rem', scrollBehavior: 'smooth', };
    },
    control: (base) => {
        return { ...base, minHeight: '1rem', height: '2rem', width: '5rem' };
    },
    valueContainer: (base) => {
        return { ...base, width: '5rem', padding: 0, height: 'inherit' };
    }
};

const YearSelect: React.FC<YearSelectProps> = ({
    date,
    changeYear,
    onKeyDown,
}) => {

    const [remappedYears, setRemappedYears] = useState<number[]>([]);
    const [currentYear] = useState<number>(new Date().getFullYear());

    useEffect(() => {
        // Initialize remappedYears with a range of years
        const initialYears = Array.from({ length: 20 }, (_, i) => currentYear - 5 + i);
        setRemappedYears(initialYears);
    }, [currentYear]);

    const handleScroll = (event: WheelEvent | TouchEvent) => {
        const target = event.currentTarget as HTMLDivElement;

        if (target.scrollTop === 0) {
            // User scrolled to the top, prepend more years
            setRemappedYears((prevYears) => {
                const firstYear = prevYears[0];
                const newYears = Array.from({ length: 10 }, (_, i) => firstYear - 10 + i);
                return [...newYears, ...prevYears];
            });
        } else if (target.scrollHeight - target.scrollTop === target.clientHeight) {
            // User scrolled to the bottom, append more years
            setRemappedYears((prevYears) => {
                const lastYear = prevYears[prevYears.length - 1];
                const newYears = Array.from({ length: 10 }, (_, i) => lastYear + i + 1);
                return [...prevYears, ...newYears];
            });
        }
    };

    const onMenuOpen = () => {
        setTimeout(() => {
            const selectedEl = document.getElementsByClassName("year-select__option--is-selected")[0];
            if (selectedEl) {
                selectedEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            }
        }, 15);
    };


    return (
        <Select
            className={'year-select'}
            styles={yearStyles}
            options={remappedYears.map((year) => ({
                value: year.toString(),
                label: year.toString(),
              }))}
            value={{ label: getYear(date).toString(), value: getYear(date).toString() }}
            onChange={(newValue: unknown) => {
                if (newValue && typeof newValue === 'object' && 'value' in newValue) {
                    changeYear(toNumber((newValue as { value: string }).value));
                }
            }}
            menuPlacement="auto"
            classNamePrefix="year-select"
            defaultValue={{
                label: getYear(new Date()).toString(),
                value: getYear(new Date()).toString(),
            }}
            onMenuScrollToTop={handleScroll}
            onMenuScrollToBottom={handleScroll}
            onMenuOpen={onMenuOpen}
            onKeyDown={onKeyDown}
            isSearchable={false}
        />
    );
};

export default YearSelect;